import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Globe } from 'lucide-react';
import { siteLinksAPI } from '../services/api';

const SOCIAL_ICON = (label) => {
  const k = (label || '').toLowerCase();
  if (k.includes('facebook')) return Facebook;
  if (k.includes('twitter') || k === 'x') return Twitter;
  if (k.includes('instagram')) return Instagram;
  if (k.includes('linkedin')) return Linkedin;
  if (k.includes('youtube')) return Youtube;
  return Globe;
};

const isHttp = (url) => /^https?:\/\//i.test(url || '');
const isMail = (url) => /^mailto:/i.test(url || '');
const isTel = (url) => /^tel:/i.test(url || '');
const isExternalAnchor = (url) => isHttp(url) || isMail(url) || isTel(url);

const FooterLink = ({ link }) => {
  const url = (link.url || '').trim();
  const target = link.target === '_blank' ? '_blank' : undefined;
  const rel = target ? 'noopener noreferrer' : undefined;

  // No URL → plain text (useful for address lines)
  if (!url || url === '#') {
    return <span data-testid={`footer-link-${link.id}`}>{link.label}</span>;
  }
  if (isExternalAnchor(url)) {
    return (
      <a
        href={url}
        target={isHttp(url) ? target : undefined}
        rel={isHttp(url) ? rel : undefined}
        className="hover:text-blue-400 transition-colors"
        data-testid={`footer-link-${link.id}`}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link
      to={url}
      target={target}
      rel={rel}
      className="hover:text-blue-400 transition-colors"
      data-testid={`footer-link-${link.id}`}
    >
      {link.label}
    </Link>
  );
};

const Footer = () => {
  const [groups, setGroups] = useState({
    footer_quick: [],
    footer_support: [],
    footer_social: [],
    footer_legal: [],
    footer_contact: [],
  });

  useEffect(() => {
    siteLinksAPI.getPublic()
      .then((d) => setGroups((g) => ({ ...g, ...d })))
      .catch(() => { /* leave defaults empty — fall back gracefully */ });
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand + Social */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">BlueSpark Australia</h3>
            <p className="text-sm mb-4">
              Premium petrol and diesel tuning boxes for enhanced performance, better fuel economy, and warranty-friendly installation.
            </p>
            <div className="flex space-x-3">
              {(groups.footer_social || []).map((l) => {
                const Icon = SOCIAL_ICON(l.label);
                return (
                  <a
                    key={l.id}
                    href={l.url}
                    target={l.target === '_blank' ? '_blank' : undefined}
                    rel={l.target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="hover:text-blue-400 transition-colors"
                    aria-label={l.label}
                    data-testid={`footer-social-${l.id}`}
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              {(groups.footer_quick || []).map((l) => (
                <li key={l.id}><FooterLink link={l} /></li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Support</h3>
            <ul className="space-y-2 text-sm">
              {(groups.footer_support || []).map((l) => (
                <li key={l.id}><FooterLink link={l} /></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm" data-testid="footer-contact-list">
              {(groups.footer_contact || []).map((l) => (
                <li key={l.id}><FooterLink link={l} /></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
          <p>&copy; {new Date().getFullYear()} BlueSpark Australia. All rights reserved.</p>
          {(groups.footer_legal || []).length > 0 && (
            <p className="mt-2 space-x-1">
              {groups.footer_legal.map((l, i) => (
                <span key={l.id}>
                  {i > 0 && <span className="text-gray-600">•</span>}
                  <span className="px-1"><FooterLink link={l} /></span>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
